import { cn } from '@elara/client-core/lib/utils';

import { IconSizes, MaterialIcon } from './MaterialIcon.js';

export interface StarRatingProps {
  /** Rating value from 0 to 5 */
  rating: number;
  /** Maximum number of stars to display (default: 5) */
  maxStars?: number;
  /** Size of the stars */
  size?: keyof typeof IconSizes;
  /** Whether to show the numeric rating value */
  showValue?: boolean;
  /** Custom className for the container */
  className?: string;
  /** Custom className for individual stars */
  starClassName?: string;
}

/**
 * StarRating component displays a visual star rating with support for half stars.
 * Renders filled, half-filled, and empty stars based on the rating value.
 */
export function StarRating({
  rating,
  maxStars = 5,
  size = 'sm',
  showValue = false,
  className,
  starClassName,
}: StarRatingProps) {
  const textSizeClasses = {
    auto: 'text-inherit',
    xxs: 'text-xs',
    xs: 'text-xs',
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base',
    xl: 'text-lg',
    '2xl': 'text-xl',
  };
  const iconSize = IconSizes[size];

  const renderStars = () => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    // Render full stars
    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <MaterialIcon key={i} icon="star" size={iconSize} filled className={cn('text-accent', starClassName)} />,
      );
    }

    // Render half star if needed
    if (hasHalfStar) {
      stars.push(
        <MaterialIcon key="half" icon="star_half" size={iconSize} className={cn('text-accent', starClassName)} />,
      );
    }

    // Render empty stars (only fill remaining positions after full + half stars)
    const emptyStars = maxStars - fullStars - (hasHalfStar ? 1 : 0);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(
        <MaterialIcon
          key={`empty-${i}`}
          icon="star"
          size={iconSize}
          filled={true}
          className={cn('text-accent opacity-10', starClassName)}
        />,
      );
    }

    return stars;
  };

  return (
    <div className={cn('flex items-center gap-1', className)}>
      {renderStars()}
      {showValue && (
        <span className={cn('text-muted-foreground ml-1', textSizeClasses[size])}>{rating.toFixed(1)}</span>
      )}
    </div>
  );
}
